import {
  Suspense,
  lazy,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import useFeedView from "../../hooks/useFeedView";
import { useInView } from "react-intersection-observer";
import MainContainer from "../../components/MainContainer";
import CollectionFilter from "../../components/CollectionFilter/CollectionFilter";
import { useDispatch, useSelector } from "react-redux";
import {
  getExploreCollections,
  getUserFavorites,
} from "../../store/slices/model/asyncThunks";
import { useSearchParams } from "react-router-dom";
import debounce from "lodash.debounce";
import {
  selectCategories,
  selectExploreCollections,
  selectExploreCollectionsFilter,
  selectExploreCollectionsKeyword,
  selectExploreCollectionsPage,
  selectExploreCollectionsSearch,
  selectFavorites,
  selectFavoritesPage,
  selectSelectedCollection,
  setSelectedCollection,
  setSelectedExploreCollectionFilter,
  setSelectedExploreCollectionKeyword,
  setSelectedExploreCollectionSearch,
} from "../../store/slices/model/slice";
import Pagination from "../../components/Pagination/Pagination";
import {
  Button,
  Column,
  Container,
  Toolbar,
  Content,
  ContentContainer,
  EmptyState,
  FeedEndMarker,
  FeedList,
  FeedLoader,
  FeedSentinel,
  FeedSkeleton,
  FeedSkeletonFrame,
  FeedSkeletonList,
  PaginationWrapper,
  SeoOnlyHeading,
  PromoBannerSection,
  PromoBannerSurface,
  PromoBannerMedia,
  PromoBannerContent,
  PromoTitle,
  PromoText,
  PromoAction,
  WaterSeparator,
} from "./HomePage.styles";
import VideoMasonrySkeletonLoader from "../../components/utils/VideoMasonrySkeletonLoader";
import ExploreItem from "../../components/ExploreItem/ExploreItem";
import TabsComponent from "../../components/utils/TabsComponent/TabsComponent";
import { useMediaQuery } from "react-responsive";
import TermsModal from "../../components/TermsModal/TermsModal";
import { selectLoggedIn } from "../../store/slices/auth/slice";
import { setSignUpModalOpen } from "../../store/slices/common/slice";
import GenerateCollectionModal from "../../components/GenerateCollectionModal/GenerateCollectionModal";
import { selectUser } from "../../store/slices/user/slice";
import {
  MEDIA_TYPE,
  PREMIUM_PLAN_NAME,
  PRO_PLAN_NAME,
} from "../../utils/constants";
import EditImageModal from "../../components/EditImageModal/EditImageModal";
import { isImageOutput } from "../../utils/utils";
import { prefetchVideos } from "../../utils/videoPrefetch";
import HomeSeoAccordions from "../../components/HomeSeoAccordions/HomeSeoAccordions";
import SeoHead from "../../components/Seo/SeoHead.js";
import {
  HOME_FAQS,
  buildBreadcrumbSchema,
  buildFaqSchema,
  buildPageTitle,
  buildWebApplicationSchema,
  buildWebsiteSchema,
} from "../../seo/siteSeo.js";

import LineWater from "../../assets/images/waterSeparator.webp";
import PromoVideo from "../../assets/videos/promo-banner-v2-optimized.mp4";
import PromoPoster from "../../assets/videos/promo-banner-v2-poster.webp";
import { ReactComponent as ParticlesIcon } from "../../assets/icons/particles.svg";

/* ExploreFeedItem only renders in feed view; lazy-load it so the heavier
   feed-only logic (progress fill RAF, sidebar buttons, etc.) doesn't sit
   in the main bundle for the grid-view majority. */
const ExploreFeedItem = lazy(
  () => import("../../components/ExploreFeedItem/ExploreFeedItem"),
);

/* Retry on transient network failures so a single blip on a flaky
   connection doesn't surface as an empty grid + error toast. The thunk
   itself still dispatches loading/success/error to the store; we only
   re-attempt after a rejection. */
const EXPLORE_FETCH_RETRY_COUNT = 2;
const EXPLORE_FETCH_RETRY_DELAY_MS = 2000;
const pause = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const HomePage = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFeedView, setIsFeedView] = useFeedView({ applyClass: true });

  const pageInStore = useSelector(selectExploreCollectionsPage);
  const pageInStoreFavorite = useSelector(selectFavoritesPage);
  const page = Number(searchParams.get("page")) || pageInStore || 1;

  const selectedCollection = useSelector(selectSelectedCollection);
  const filter = useSelector(selectExploreCollectionsFilter);
  const savedSearch = useSelector(selectExploreCollectionsSearch);
  const savedKeyword = useSelector(selectExploreCollectionsKeyword);

  const [searchValue, setSearchValue] = useState(() => savedSearch ?? "");
  const [search, setSearch] = useState(savedSearch ?? "");

  const exploreData = useSelector(selectExploreCollections);
  const favoriteData = useSelector(selectFavorites);
  const loggedIn = useSelector(selectLoggedIn);
  const { data: categories, error: errorCategories } =
    useSelector(selectCategories);
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false);
  const [generateInitialPrompt, setGenerateInitialPrompt] = useState("");
  const [isGenerateCollectionModalOpen, setIsGenerateCollectionModalOpen] =
    useState(false);

  const [isEditImageModalOpen, setIsEditImageModalOpen] = useState(false);

  const isLargeDesktop = useMediaQuery({ maxWidth: 1600 });
  const isDesktop = useMediaQuery({ maxWidth: 1440 });
  const isTable = useMediaQuery({ maxWidth: 950 });
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const error = exploreData.error || errorCategories;

  const user = useSelector(selectUser);

  const isProEnabled =
    [PRO_PLAN_NAME, PREMIUM_PLAN_NAME].includes(user?.subscription?.name) &&
    user?.subscription?.endDate &&
    new Date(user?.subscription?.endDate) > new Date();

  /* Fire-and-forget wrapper around getExploreCollections that retries
     on rejection. Errors after the final attempt are swallowed because
     the slice already records the error state for the UI to render. */
  const requestExploreCollections = useCallback(
    (params) => {
      const run = async () => {
        for (
          let attempt = 0;
          attempt <= EXPLORE_FETCH_RETRY_COUNT;
          attempt += 1
        ) {
          try {
            return await dispatch(getExploreCollections(params)).unwrap();
          } catch (error) {
            if (attempt === EXPLORE_FETCH_RETRY_COUNT) throw error;
            await pause(EXPLORE_FETCH_RETRY_DELAY_MS);
          }
        }
        return null;
      };
      void run().catch(() => {});
    },
    [dispatch],
  );

  useEffect(() => {
    if (
      !Number(searchParams.get("page")) &&
      ((filter !== "FAVORITES" && pageInStore) ||
        (filter === "FAVORITES" && pageInStoreFavorite))
    ) {
      if (filter !== "FAVORITES" && pageInStore && pageInStore > 1) {
        setSearchParams({ page: pageInStore });
      }
      if (
        filter === "FAVORITES" &&
        pageInStoreFavorite &&
        pageInStoreFavorite > 1
      ) {
        setSearchParams({ page: pageInStoreFavorite });
      }

      //   else {
      //     setSearchParams({ page: 1 });
      //   }
    }
  }, [filter, pageInStore, pageInStoreFavorite, searchParams, setSearchParams]);

  //   useEffect(() => {
  //     if (!filter) {
  //       setSearchParams({ page: 1 });
  //     }
  //   }, [filter, setSearchParams]);

  useEffect(() => {
    if (categories?.length && !filter) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
    if (!categories?.length) {
      const keyword = searchParams.get("category");
      if (keyword) {
        dispatch(setSelectedExploreCollectionKeyword(keyword));
        searchParams.delete("category");
        setSearchParams(searchParams);
      }

      requestExploreCollections({
        page: page || 1,
        search: "",
        ...(keyword && { keyword }),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, dispatch, filter]);

  useEffect(() => {
    if (!loggedIn && filter === "FAVORITES" && categories?.length) {
      dispatch(setSelectedExploreCollectionFilter(categories[0]._id));
    }
  }, [loggedIn, filter, categories, dispatch]);

  useEffect(() => {
    const hasSeenTerms = localStorage.getItem("hasSeenTerms");
    if (!hasSeenTerms) {
      setIsTermsModalOpen(true);
      localStorage.setItem("hasSeenTerms", "true");
    }
  }, []);

  const numOfColumns = useMemo(() => {
    switch (true) {
      case isMobile:
        return 2;
      case isTable:
        return 3;
      case isDesktop:
        return 4;
      case isLargeDesktop:
        return 5;
      default:
        return 6;
    }
  }, [isLargeDesktop, isDesktop, isTable, isMobile]);

  const data = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.data;
    } else {
      return exploreData.data;
    }
  }, [filter, favoriteData, exploreData]);

  const total = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.total;
    } else {
      return exploreData.total;
    }
  }, [filter, favoriteData, exploreData]);

  const loading = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.loading;
    } else {
      return exploreData.loading;
    }
  }, [filter, favoriteData, exploreData]);

  const maxReached = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.maxReached;
    }
    return exploreData.maxReached;
  }, [filter, favoriteData, exploreData]);

  const activePage = useMemo(() => {
    if (filter === "FAVORITES") {
      return favoriteData.page || 1;
    }
    return exploreData.page || 1;
  }, [filter, favoriteData, exploreData]);

  const [feedScrollEl, setFeedScrollEl] = useState(null);

  const { ref: feedSentinelRef, inView: feedSentinelInView } = useInView({
    root: feedScrollEl,
    rootMargin: "800px 0px",
    threshold: 0,
  });

  const [activeIndex, setActiveIndex] = useState(0);
  const [itemHeight, setItemHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 0,
  );

  useLayoutEffect(() => {
    if (!feedScrollEl) return;
    const update = () => {
      const h = feedScrollEl.clientHeight;
      if (h > 0) {
        setItemHeight((prev) => (prev === h ? prev : h));
      }
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(feedScrollEl);
    return () => ro.disconnect();
  }, [feedScrollEl]);

  useEffect(() => {
    if (!feedScrollEl) return;
    setActiveIndex(0);
  }, [feedScrollEl]);

  useEffect(() => {
    if (!feedScrollEl || !itemHeight) return;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = 0;
        const idx = Math.max(
          0,
          Math.round(feedScrollEl.scrollTop / itemHeight),
        );
        setActiveIndex((prev) => (prev === idx ? prev : idx));
      });
    };
    feedScrollEl.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      feedScrollEl.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [feedScrollEl, itemHeight]);

  useEffect(() => {
    if (!data?.length) return;
    setActiveIndex((prev) => Math.min(prev, data.length - 1));
  }, [data?.length]);

  useEffect(() => {
    if (!isFeedView || !data?.length) return;
    const urls = [];
    for (let offset = 2; offset <= 4; offset += 1) {
      const it = data[activeIndex + offset];
      if (!it) continue;
      const isVideo = it?.output?.type === MEDIA_TYPE.VIDEO;
      const url = isVideo ? it?.output?.video?.url : null;
      if (url) urls.push(url);
    }
    if (urls.length) prefetchVideos(urls);
  }, [activeIndex, data, isFeedView]);

  useEffect(() => {
    if (!isFeedView) return;
    if (!feedSentinelInView) return;
    if (loading) return;
    if (maxReached) return;
    if (!data?.length) return;

    setPage((activePage || 1) + 1, { silent: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedSentinelInView, isFeedView, loading, maxReached, activePage]);

  const changeHandler = useCallback(
    (text) => {
      if (!loggedIn) {
        dispatch(setSignUpModalOpen(true));
        return;
      }

      searchParams.delete("page");
      setSearchParams(searchParams);
      if (search !== text) {
        setSearch(text);
        dispatch(setSelectedExploreCollectionSearch(text));
        if (filter !== "FAVORITES") {
          requestExploreCollections({
            page: 1,
            search: text,
            categoryId: filter,
            keyword: savedKeyword,
          });
        } else {
          dispatch(
            getUserFavorites({ page: 1, search: text, keyword: savedKeyword }),
          );
        }
      }
    },
    [
      searchParams,
      setSearchParams,
      search,
      dispatch,
      filter,
      loggedIn,
      savedKeyword,
      requestExploreCollections,
    ],
  );

  const debouncedChangeHandler = useMemo(
    () => debounce(changeHandler, 300),
    [changeHandler],
  );

  const setPage = (page, options) => {
    const silent = options?.silent;

    if (!silent) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "instant",
      });
    }

    if (page > 1) {
      searchParams.set("page", page);
    } else {
      searchParams.delete("page");
    }

    setSearchParams(searchParams, { replace: !!silent });
    if (filter !== "FAVORITES") {
      requestExploreCollections({
        page,
        search: searchValue,
        categoryId: filter,
        keyword: savedKeyword,
        append: !!silent,
      });
    } else {
      dispatch(
        getUserFavorites({
          page,
          search: searchValue,
          keyword: savedKeyword,
          append: !!silent,
        }),
      );
    }
  };

  useEffect(() => {
    if (filter === "FAVORITES" && !data) {
      searchParams.delete("page");
      setSearchParams(searchParams);
      dispatch(
        getUserFavorites({
          page: 1,
          search: "",
          keyword: savedKeyword,
        }),
      );
    }
  }, [filter, dispatch, data, searchParams, setSearchParams, savedKeyword]);

  useEffect(() => {
    if (!filter || data) return;

    if (filter !== "FAVORITES") {
      searchParams.delete("page");
      setSearchParams(searchParams);

      requestExploreCollections({
        page: 1,
        search: "",
        categoryId: filter,
        keyword: savedKeyword,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, filter, data, searchParams, setSearchParams, savedKeyword]);

  const onChangeFilter = (filter) => {
    dispatch(setSelectedExploreCollectionFilter(filter));
    searchParams.delete("page");
    setSearchParams(searchParams);

    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page: 1,
          search: searchValue,
          keyword: savedKeyword,
        }),
      );
    } else {
      requestExploreCollections({
        page: 1,
        search: searchValue,
        categoryId: filter,
        keyword: savedKeyword,
      });
    }
  };

  const onChangeKeyword = (keyword) => {
    dispatch(setSelectedExploreCollectionKeyword(keyword));
    searchParams.delete("page");
    setSearchParams(searchParams);
    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page: 1,
          search: searchValue,
          keyword,
        }),
      );
    } else {
      requestExploreCollections({
        page: 1,
        search: searchValue,
        categoryId: filter,
        keyword,
      });
    }
  };

  const handleUnauthorizedSearch = useCallback(() => {
    dispatch(setSignUpModalOpen(true));
  }, [dispatch]);

  const handlePromoAction = useCallback(() => {
    if (!loggedIn) {
      dispatch(setSignUpModalOpen(true));
      return;
    }

    document.getElementById("explore-toolbar")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, [dispatch, loggedIn]);

  const retryCurrentExploreRequest = useCallback(() => {
    const retryPage = page || activePage || 1;

    if (filter === "FAVORITES") {
      dispatch(
        getUserFavorites({
          page: retryPage,
          search: searchValue,
          keyword: savedKeyword,
        }),
      );
      return;
    }

    requestExploreCollections({
      page: retryPage,
      search: searchValue,
      categoryId: filter,
      keyword: savedKeyword,
    });
  }, [
    activePage,
    dispatch,
    filter,
    page,
    requestExploreCollections,
    savedKeyword,
    searchValue,
  ]);

  const handleToggleViewMode = useCallback(
    (targetItemId) => {
      const explicitTarget =
        typeof targetItemId === "string" && targetItemId ? targetItemId : null;
      const feedCurrentId =
        isFeedView && data?.length
          ? data[Math.min(activeIndex, data.length - 1)]?._id
          : null;
      const resolvedTarget = explicitTarget || feedCurrentId || null;

      setIsFeedView((prev) => !prev);

      if (!resolvedTarget) {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "instant",
        });
        return;
      }

      const scrollToTarget = () => {
        const el = document.getElementById(resolvedTarget);
        if (!el) return false;
        el.scrollIntoView({ block: "center", behavior: "instant" });
        return true;
      };

      requestAnimationFrame(() => {
        if (scrollToTarget()) return;
        requestAnimationFrame(() => {
          if (scrollToTarget()) return;
          requestAnimationFrame(() => {
            scrollToTarget();
          });
        });
      });
    },
    [isFeedView, setIsFeedView, data, activeIndex],
  );

  const handleExploreItemClick = (item) => {
    if (loggedIn) {
      dispatch(setSelectedCollection(item));
      if (isImageOutput(item.modelType ?? item.modelName)) {
        setIsEditImageModalOpen(true);
      } else {
        setIsGenerateCollectionModalOpen(true);
      }
    } else {
      dispatch(setSignUpModalOpen(true));
    }
  };

  const jsonLd = useMemo(
    () => [
      buildWebsiteSchema(),
      buildWebApplicationSchema({
        name: "Fapify AI Porn Generator",
        description:
          "Fapify combines AI porn templates, undress AI, image-to-video generation, nudify-compatible edits, and repeatable NSFW workflows.",
        url: "https://www.fapify.com/",
      }),
      buildBreadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "AI Porn Generator", path: "/ai-porn-generator" },
      ]),
      buildFaqSchema(HOME_FAQS),
    ],
    [],
  );

  return (
    <MainContainer
      hideTabs={false}
      setSearchValue={setSearchValue}
      fullWidth
      hideOverflowX={isFeedView}
      isFeedView={isFeedView}
      onToggleViewMode={handleToggleViewMode}
      showViewModeToggle
    >
      <SeoHead
        title={buildPageTitle("AI Porn Generator, Undress AI & NSFW AI")}
        description="Fapify is an AI porn generator with public AI porn templates, undress AI, image-to-video workflows, nudify-compatible edits, NSFW AI tools, and reusable AI girlfriend-style workflows."
        path="/"
        noindex={page > 1}
        jsonLd={jsonLd}
      />
      <Container>
        <SeoOnlyHeading>
          AI Porn Generator for Undress AI, NSFW AI, and Custom Templates
        </SeoOnlyHeading>
        {!isFeedView && (
          <>
            {loggedIn === false && (
              <PromoBannerSection aria-labelledby="home-promo-title">
                <PromoBannerSurface>
                  <PromoBannerMedia
                    src={PromoVideo}
                    poster={PromoPoster}
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="auto"
                    aria-hidden="true"
                  />
                  <PromoBannerContent>
                    <PromoTitle id="home-promo-title">
                      <span>UPLOAD ANY PHOTO OF HER AND GET</span>
                      <span>A HOT VIDEO IN JUST 2 MINUTES 🔥</span>
                    </PromoTitle>
                    <PromoText>
                      Your first generation is completely free!
                    </PromoText>
                    <PromoAction type="button" onClick={handlePromoAction}>
                      <ParticlesIcon aria-hidden="true" />
                      GENERATE NOW
                    </PromoAction>
                  </PromoBannerContent>
                </PromoBannerSurface>
              </PromoBannerSection>
            )}
          </>
        )}

        <Toolbar id="explore-toolbar" $feed={isFeedView}>
          {/* Always rendered — visible in both grid and feed layouts so the
              view-mode toggle is reachable from either. */}
          <TabsComponent
            isFeedView={isFeedView}
            onToggleViewMode={handleToggleViewMode}
            showViewModeToggle
          />
          {!isFeedView && (
            <CollectionFilter
              loading={loading}
              filter={filter}
              setFilter={onChangeFilter}
              searchValue={searchValue}
              onSearchChange={(value) => {
                if (!loggedIn) {
                  handleUnauthorizedSearch();
                  return;
                }
                setSearchValue(value);
                debouncedChangeHandler(value);
              }}
              onChangeKeyword={onChangeKeyword}
              isProEnabled={isProEnabled}
              searchRequiresAuth
              onUnauthorizedSearch={handleUnauthorizedSearch}
            />
          )}
        </Toolbar>

        <ContentContainer>
          {!isFeedView && <WaterSeparator src={LineWater} alt="Line Water" />}
          {error ? (
            <Content>
              <EmptyState $isFeedView={isFeedView}>
                <h3>We’re having trouble loading videos</h3>
                <p>
                  This is usually temporary. Please check your connection and
                  try again.
                </p>
                <Button
                  onClick={() => {
                    retryCurrentExploreRequest();
                  }}
                >
                  Retry
                </Button>
              </EmptyState>
            </Content>
          ) : !data || (loading && !data.length) ? (
            isFeedView ? (
              <FeedSkeletonList>
                <FeedSkeleton>
                  <FeedSkeletonFrame />
                </FeedSkeleton>
              </FeedSkeletonList>
            ) : (
              <VideoMasonrySkeletonLoader count={24} fullWidth mobileTwoRows />
            )
          ) : (
            <>
              {!data?.length ? (
                <EmptyState $isFeedView={isFeedView}>
                  <h3>No videos found</h3>
                  <p>There are no videos to display at the moment.</p>
                </EmptyState>
              ) : isFeedView ? (
                <Suspense
                  fallback={
                    <FeedSkeletonList>
                      <FeedSkeleton>
                        <FeedSkeletonFrame />
                      </FeedSkeleton>
                    </FeedSkeletonList>
                  }
                >
                  {(() => {
                    const WINDOW_HALF = 3;
                    const total = data.length;
                    const startIdx = Math.max(0, activeIndex - WINDOW_HALF);
                    const endIdx = Math.min(
                      total,
                      activeIndex + WINDOW_HALF + 1,
                    );
                    const before = startIdx * itemHeight;
                    const after = Math.max(0, total - endIdx) * itemHeight;
                    return (
                      <FeedList ref={setFeedScrollEl}>
                        {before > 0 && (
                          <div
                            aria-hidden
                            style={{
                              height: `${before}px`,
                              flexShrink: 0,
                              pointerEvents: "none",
                            }}
                          />
                        )}
                        {data.slice(startIdx, endIdx).map((item, i) => {
                          const realIdx = startIdx + i;
                          const distance = Math.abs(realIdx - activeIndex);
                          const priority = distance <= 1 ? "near" : "far";
                          return (
                            <ExploreFeedItem
                              key={item._id}
                              item={item}
                              priority={priority}
                              onItemClick={() => handleExploreItemClick(item)}
                            />
                          );
                        })}
                        {after > 0 && (
                          <div
                            aria-hidden
                            style={{
                              height: `${after}px`,
                              flexShrink: 0,
                              pointerEvents: "none",
                            }}
                          />
                        )}
                        <FeedSentinel ref={feedSentinelRef} aria-hidden />
                        {!maxReached && loading && data.length > 0 && (
                          <FeedLoader
                            aria-live="polite"
                            aria-label="Loading more"
                          >
                            <span />
                            <span />
                            <span />
                          </FeedLoader>
                        )}
                        {maxReached && data.length > 0 && (
                          <FeedEndMarker>You're all caught up</FeedEndMarker>
                        )}
                      </FeedList>
                    );
                  })()}
                </Suspense>
              ) : (
                <Content>
                  {Array.from({ length: numOfColumns }).map((_, colIndex) => {
                    return (
                      <Column key={colIndex}>
                        {data
                          .filter((_, i) => i % numOfColumns === colIndex)
                          .map((item) => {
                            return (
                              <ExploreItem
                                key={item._id}
                                item={item}
                                isMobile={isMobile}
                                onItemClick={() => handleExploreItemClick(item)}
                              />
                            );
                          })}
                      </Column>
                    );
                  })}
                </Content>
              )}
            </>
          )}
        </ContentContainer>
        {!isFeedView && (
          <PaginationWrapper>
            <Pagination
              currentPage={page}
              totalCount={total}
              perPage={20}
              onPageChange={(page) => setPage(page)}
            />
          </PaginationWrapper>
        )}
        {!isFeedView && <HomeSeoAccordions />}
      </Container>

      <TermsModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
      />

      <GenerateCollectionModal
        isOpen={isGenerateCollectionModalOpen}
        onClose={() => {
          setIsGenerateCollectionModalOpen(false);
          setGenerateInitialPrompt("");
        }}
        initialPrompt={generateInitialPrompt}
        onChangeFilter={onChangeFilter}
      />

      <EditImageModal
        isOpen={isEditImageModalOpen}
        collection={selectedCollection}
        onClose={() => setIsEditImageModalOpen(false)}
      />
    </MainContainer>
  );
};

export default HomePage;
